import type {
  BaseRecord,
  CreateParams,
  CreateResponse,
  CustomParams,
  CustomResponse,
  DataProvider,
  DeleteOneParams,
  DeleteOneResponse,
  GetListParams,
  GetListResponse,
  GetOneParams,
  GetOneResponse,
  UpdateParams
} from '@refinedev/core';

import { resourceConfig } from '../resourceConfig';
import type { DataProviderEnhancer } from '../types';

export const withConfigValidationEnhancer: DataProviderEnhancer = (
  provider: DataProvider
) => {
  return {
    ...provider,
    getOne: async <TData extends BaseRecord = BaseRecord>(
      params: GetOneParams
    ): Promise<GetOneResponse<TData>> => {
      const config = resourceConfig[params.resource];

      throwIfNotConfig(params.resource, config);

      return provider.getOne<TData>(params);
    },

    getList: async <TData extends BaseRecord = BaseRecord>(
      params: GetListParams
    ): Promise<GetListResponse<TData>> => {
      const config = resourceConfig[params.resource];

      throwIfNotConfig(params.resource, config);

      return provider.getList<TData>(params);
    },

    create: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: CreateParams<TVariables>
    ): Promise<CreateResponse<TData>> => {
      const config = resourceConfig[params.resource];

      throwIfNotConfig(params.resource, config);

      return provider.create<TData>(params as CreateParams<object>);
    },

    update: async <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: UpdateParams<TVariables>
    ): Promise<CreateResponse<TData>> => {
      const config = resourceConfig[params.resource];

      throwIfNotConfig(params.resource, config);

      return provider.update<TData>(params as UpdateParams<object>);
    },

    deleteOne: <TData extends BaseRecord = BaseRecord, TVariables = object>(
      params: DeleteOneParams<TVariables>
    ): Promise<DeleteOneResponse<TData>> => {
      const config = resourceConfig[params.resource];

      throwIfNotConfig(params.resource, config);

      return provider.deleteOne<TData>(params as DeleteOneParams<object>);
    },

    custom: <
      TData extends BaseRecord = BaseRecord,
      TQuery = unknown,
      TPayload = unknown
    >(
      params: CustomParams<TQuery, TPayload>
    ): Promise<CustomResponse<TData>> => {
      const config = resourceConfig[params.url];
      const path = config?.path ?? params.url;

      throwIfNotConfig(params.url, path);
      return provider.custom!<TData>(params);
    }
  };
};

function throwIfNotConfig(
  resource?: string,
  config?: string | { path: string }
) {
  if (!config) {
    throw new Error(
      resource
        ? `Resource ${resource} is not configured`
        : 'Resource is not configured'
    );
  }
}
